import type { Client, Course, DonneesOps, Encaissement, Facture, MoyenEncaissement } from './types'

/**
 * Encaissement d'une course de particulier (migration 0033) — logique PURE, testée.
 *
 * ⚠️ Un encaissement n'existe que s'il est ENREGISTRÉ. La comptabilité constate la créance à la
 * livraison (411) et n'en éteint la part encaissée (551 / 571 / 521) qu'à partir d'un paiement
 * enregistré ici : plus aucune trésorerie fabriquée. Le montant et les frais sont de toute façon
 * RECALCULÉS par la base (`enregistrer_encaissement_course`) ; ce module sert à contrôler avant
 * l'envoi, à prévisualiser les frais et à traduire les refus.
 */

/** Les moyens proposés, dans l'ordre d'affichage. Les valeurs sont celles de la base. */
export const MOYENS_ENCAISSEMENT: readonly { valeur: MoyenEncaissement; libelle: string }[] = [
  { valeur: 'especes', libelle: 'Espèces' },
  { valeur: 'wave', libelle: 'Wave' },
  { valeur: 'orange_money', libelle: 'Orange Money' },
  { valeur: 'autre', libelle: 'Autre (virement…)' },
]

export function libelleMoyenEncaissement(m: string | null | undefined): string {
  return MOYENS_ENCAISSEMENT.find((x) => x.valeur === m)?.libelle ?? ''
}

export function moyenEncaissementValide(m: string): m is MoyenEncaissement {
  return MOYENS_ENCAISSEMENT.some((x) => x.valeur === m)
}

/** Wave et Orange Money sont les deux moyens qui portent des frais (compte 551, frais en 631). */
export function estMobileMoney(m: string | null | undefined): boolean {
  return m === 'wave' || m === 'orange_money'
}

/**
 * Frais mobile money d'un encaissement : montant × taux, arrondi au franc, DEMI VERS LE HAUT — la règle de la
 * base (`floor(montant × taux + 0.5)`). Calculé sur le taux en dix-millièmes entiers (la colonne est un
 * `numeric(6,4)`) : aucune erreur de flottant (2 500 × 0,015 = 37,5 → 38, pas 37).
 */
export function fraisMobileMoney(montant: number, taux: number): number {
  if (!(montant > 0) || !(taux > 0)) return 0
  const dixMilliemes = Math.round(taux * 10000)
  return Math.floor((montant * dixMilliemes + 5000) / 10000)
}

/** Le taux d'encaissement du moyen, tiré de la grille (fraction). 0 pour les espèces et « autre ». */
export function tauxEncaissement(
  tarifs: { frais_mm_encaissement_wave: number; frais_mm_encaissement_om: number } | null | undefined,
  moyen: MoyenEncaissement,
): number {
  if (!tarifs) return 0
  if (moyen === 'wave') return tarifs.frais_mm_encaissement_wave
  if (moyen === 'orange_money') return tarifs.frais_mm_encaissement_om
  return 0
}

/** Ce qui a été payé (paiements réussis) et ce qui reste dû sur une course. */
export function soldeCourse(
  course: Pick<Course, 'id' | 'price_fcfa'>,
  encaissements: readonly Encaissement[],
): { paye: number; du: number } {
  const paye = encaissements
    .filter((p) => p.course_id === course.id && p.status === 'succeeded')
    .reduce((s, p) => s + p.amount_fcfa, 0)
  return { paye, du: Math.max(0, course.price_fcfa - paye) }
}

export type PossibiliteEncaisser = { ok: true; du: number } | { ok: false; raison: string }

/**
 * Peut-on proposer « Encaissement reçu » sur cette course ? (La base tranche en dernier ressort ; ceci évite
 * d'afficher un bouton qu'elle refuserait.) Course LIVRÉE d'un client PARTICULIER, non portée par une facture,
 * avec un reste dû.
 */
export function peutEncaisser(
  course: Pick<Course, 'id' | 'statut' | 'price_fcfa'>,
  client: Pick<Client, 'type'> | undefined,
  facturees: ReadonlySet<string>,
  encaissements: readonly Encaissement[],
): PossibiliteEncaisser {
  if (course.statut !== 'livree') return { ok: false, raison: "On n'encaisse qu'une course livrée." }
  if (client && client.type !== 'particulier') return { ok: false, raison: "Les courses d'une PME se règlent par facture." }
  if (facturees.has(course.id)) return { ok: false, raison: 'Cette course est portée par une facture : elle se règle par la facture.' }
  const { du } = soldeCourse(course, encaissements)
  if (du <= 0) return { ok: false, raison: 'Cette course est entièrement encaissée.' }
  return { ok: true, du }
}

/** Libellé court de l'état d'encaissement d'une course, pour les listes. */
export function etatEncaissement(
  course: Pick<Course, 'id' | 'price_fcfa'>,
  encaissements: readonly Encaissement[],
): { libelle: string; pastille: 'b-green' | 'b-gold' | 'b-gray' } {
  const { paye, du } = soldeCourse(course, encaissements)
  if (course.price_fcfa > 0 && du === 0) return { libelle: 'Encaissée', pastille: 'b-green' }
  if (paye > 0) return { libelle: 'Partielle', pastille: 'b-gold' }
  return { libelle: 'Non encaissée', pastille: 'b-gray' }
}

/** Motifs de refus d'un encaissement avant tout appel réseau (vide = acceptable). */
export function controlerEncaissement(
  e: { moyen: string; montant: number; date: string; du: number; livreeLe: string | null },
  aujourdHui: string,
  verrou: string | null = null,
): string[] {
  const motifs: string[] = []
  if (!moyenEncaissementValide(e.moyen)) motifs.push('Choisissez un moyen de paiement.')
  if (!Number.isInteger(e.montant) || e.montant <= 0) motifs.push('Le montant doit être un nombre entier de francs, supérieur à zéro.')
  else if (e.montant > e.du) motifs.push(`Il ne reste que ${e.du} F à encaisser sur cette course.`)
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(e.date) && !Number.isNaN(new Date(`${e.date}T00:00:00Z`).getTime())
  if (!dateOk) motifs.push('Date de paiement invalide.')
  else {
    if (e.date > aujourdHui) motifs.push('La date de paiement ne peut pas être dans le futur.')
    if (e.livreeLe && e.date < e.livreeLe.slice(0, 10)) motifs.push('Le paiement ne peut pas précéder la livraison.')
    if (verrou && e.date <= verrou) motifs.push(`La période est clôturée jusqu'au ${dateFr(verrou)}.`)
  }
  return motifs
}

function dateFr(iso: string): string {
  const [a, m, j] = iso.slice(0, 10).split('-')
  return `${j}/${m}/${a}`
}

/** Les refus de la base (`raise exception '<code>'`), en français. */
const MESSAGES_ENCAISSEMENT: Record<string, string> = {
  forbidden: "Votre rôle ne permet pas d'enregistrer ou d'annuler un encaissement.",
  moyen_invalide: 'Moyen de paiement invalide.',
  course_sans_client: "Cette course n'est rattachée à aucun client.",
  course_pme_sur_facture: "Les courses d'une PME se règlent par facture, pas course par course.",
  course_non_livree: "On n'encaisse qu'une course livrée : avant, la créance n'existe pas.",
  course_facturee: 'Cette course est portée par une facture : elle se règle par la facture.',
  montant_invalide: 'Le montant doit être un nombre entier de francs, supérieur à zéro.',
  montant_superieur_au_du: 'Le montant dépasse ce qui reste à encaisser sur cette course.',
  date_future: 'La date ne peut pas être dans le futur.',
  date_avant_livraison: 'Le paiement ne peut pas précéder la livraison.',
  date_avant_emission: "Le règlement ne peut pas précéder l'émission de la facture.",
  encaissement_deja_annule: 'Cet encaissement est déjà annulé.',
  facture_deja_reglee: 'Cette facture est déjà réglée.',
  facture_annulee: 'Cette facture est annulée : on ne la règle pas.',
  periode_cloturee: 'Cette date tombe dans une période clôturée : rouvrez l’exercice ou datez le paiement après.',
}

/** Erreur Postgres / PostgREST → phrase. Le `detail` écrit par la base (restant dû…) est ajouté quand il aide. */
export function traduireErreurEncaissement(erreur: unknown): string {
  const o = (erreur && typeof erreur === 'object' ? erreur : {}) as { message?: unknown; details?: unknown; detail?: unknown }
  const message = typeof o.message === 'string' ? o.message : typeof erreur === 'string' ? erreur : ''
  const detail = typeof o.details === 'string' ? o.details : typeof o.detail === 'string' ? o.detail : ''
  for (const [code, phrase] of Object.entries(MESSAGES_ENCAISSEMENT)) {
    if (message.includes(code)) {
      if (code === 'periode_cloturee' && detail) return detail
      if (code === 'montant_superieur_au_du' && detail) return detail
      return phrase
    }
  }
  if (/Could not find the function|PGRST202|function .* does not exist/i.test(message))
    return 'Disponible après la mise à jour de la base (migration 0033).'
  return message || 'Erreur inconnue.'
}

/**
 * « Encaissé aujourd'hui », calculé localement : paiements de courses ENREGISTRÉS (réussis) du jour + règlements
 * de factures du jour. Repli quand la base ne rend pas l'agrégat ; même définition que la RPC
 * `encaisse_du_jour` — et que la comptabilité. `jour` = date de Bamako (UTC+0, sans changement d'heure).
 */
export function encaisseDuJour(
  d: Pick<DonneesOps, 'encaissements' | 'invoices'>,
  jour: string,
): { courses: number; factures: number; total: number } {
  const courses = d.encaissements
    .filter((p) => p.status === 'succeeded' && p.paid_at.slice(0, 10) === jour)
    .reduce((s, p) => s + p.amount_fcfa, 0)
  const factures = d.invoices
    .filter((f: Facture) => (f.status === 'issued' || f.status === 'paid') && f.paid_at?.slice(0, 10) === jour)
    .reduce((s, f) => s + f.total_ttc_fcfa, 0)
  return { courses, factures, total: courses + factures }
}

/** La date de Bamako (UTC+0) au format AAAA-MM-JJ. */
export function jourBamako(maintenant: Date = new Date()): string {
  return maintenant.toISOString().slice(0, 10)
}
